<template>
    <div class="wizard-component card" :class="wizardClasses" data-cy="wizard-component">
        <div v-if="title || subtitle || $slots.header" class="wizard-component__header">
            <div>
                <span v-if="eyebrow" class="wizard-component__eyebrow">{{ eyebrow }}</span>
                <h5 v-if="title" class="wizard-component__title">{{ title }}</h5>
                <p v-if="subtitle" class="wizard-component__subtitle">{{ subtitle }}</p>
            </div>
            <slot name="header" :active-step="activeStep" :active-index="activeIndex"></slot>
        </div>

        <div class="wizard-component__body">
            <div class="wizard-component__mobile-step-header" data-cy="wizard-mobile-step-header">
                <button
                    type="button"
                    class="wizard-component__mobile-step-arrow"
                    :disabled="isFirstStep || isChanging"
                    aria-label="Etapa anterior"
                    data-cy="wizard-mobile-previous"
                    @click="previousStep"
                >
                    <i :class="previousIcon"></i>
                </button>

                <div class="wizard-component__mobile-step-card">
                    <div class="wizard-component__mobile-step-topline">
                        <span class="wizard-component__mobile-step-counter">{{ stepPositionLabel }}</span>
                        <span v-if="activeStep.completed" class="wizard-component__step-completed-label">
                            {{ completedLabel }}
                        </span>
                        <span v-else-if="activeStep.optional" class="wizard-component__step-optional">
                            {{ optionalLabel }}
                        </span>
                        <span v-else-if="showRequiredBadge" class="wizard-component__step-required-label">
                            {{ requiredLabel }}
                        </span>
                    </div>

                    <div class="wizard-component__mobile-step-main">
                        <span class="wizard-component__mobile-step-marker">
                            <i v-if="activeStep.icon" :class="activeStep.icon"></i>
                            <span v-else>{{ activeIndex + 1 }}</span>
                        </span>
                        <div class="wizard-component__mobile-step-text">
                            <h6>{{ activeStep.label }}</h6>
                            <p v-if="activeStep.description">{{ activeStep.description }}</p>
                        </div>
                    </div>

                    <div class="wizard-component__mobile-step-progress" aria-hidden="true">
                        <span :style="{ width: stepProgressPercent }"></span>
                    </div>
                </div>

                <button
                    type="button"
                    class="wizard-component__mobile-step-arrow"
                    :disabled="isLastStep || isChanging || nextDisabled"
                    aria-label="Proxima etapa"
                    data-cy="wizard-mobile-next"
                    @click="nextStep"
                >
                    <i :class="nextIcon"></i>
                </button>
            </div>

            <nav class="wizard-component__nav" aria-label="Etapas do cadastro">
                <button
                    v-for="(step, index) in normalizedSteps"
                    :key="step.key"
                    type="button"
                    class="wizard-component__step"
                    :class="getStepClasses(step, index)"
                    :disabled="!canClickStep(step, index) || isChanging"
                    :aria-current="index === activeIndex ? 'step' : null"
                    :data-cy="`wizard-step-${index + 1}`"
                    @click="goToStep(index)"
                >
                    <span class="wizard-component__step-marker">
                        <i v-if="step.icon" :class="step.icon"></i>
                        <span v-else>{{ index + 1 }}</span>
                    </span>
                    <span class="wizard-component__step-content">
                        <span class="wizard-component__step-label-row">
                            <span class="wizard-component__step-label">{{ step.label }}</span>
                            <span v-if="showRequiredBadge && !step.optional" class="wizard-component__step-required-asterisk">*</span>
                            <span v-if="step.optional && !step.completed" class="wizard-component__step-optional">
                                {{ optionalLabel }}
                            </span>
                            <span v-else-if="showRequiredBadge && !step.completed" class="wizard-component__step-required-label">
                                {{ requiredLabel }}
                            </span>
                            <span v-if="step.completed" class="wizard-component__step-completed-label">
                                {{ completedLabel }}
                            </span>
                        </span>
                        <small v-if="step.description" class="wizard-component__step-description">
                            {{ step.description }}
                        </small>
                    </span>
                </button>
            </nav>

            <section class="wizard-component__content">
                <slot
                    :name="activeStep.slot"
                    :step="activeStep"
                    :active-index="activeIndex"
                    :is-first="isFirstStep"
                    :is-last="isLastStep"
                >
                    <slot
                        :step="activeStep"
                        :active-index="activeIndex"
                        :is-first="isFirstStep"
                        :is-last="isLastStep"
                    ></slot>
                </slot>
            </section>
        </div>

        <div v-if="showActions" class="wizard-component__footer">
            <button
                type="button"
                class="btn wizard-component__action wizard-component__action--previous"
                :disabled="isFirstStep || isChanging"
                data-cy="wizard-previous"
                @click="previousStep"
            >
                <i :class="previousIcon"></i>
                {{ previousLabel }}
            </button>

            <div class="wizard-component__footer-actions">
                <slot name="actions" :active-step="activeStep" :active-index="activeIndex"></slot>
                <button
                    v-if="!isLastStep"
                    type="button"
                    class="btn btn-primary wizard-component__action wizard-component__action--next"
                    :disabled="isChanging || nextDisabled"
                    data-cy="wizard-next"
                    @click="nextStep"
                >
                    {{ nextLabel }}
                    <i :class="nextIcon"></i>
                </button>
                <button
                    v-if="showFinishButton"
                    type="button"
                    class="btn btn-primary wizard-component__action wizard-component__action--finish"
                    :disabled="isChanging || finishDisabled"
                    data-cy="wizard-finish"
                    @click="$emit('finish', activeStep, activeIndex)"
                >
                    {{ finishLabel }}
                    <i v-if="finishIcon" :class="finishIcon"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WizardComponent',
    props: {
        steps: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: [Number, String],
            default: 0,
        },
        navigationPosition: {
            type: String,
            default: 'top',
            validator(value) {
                return ['top', 'left'].includes(value);
            },
        },
        clickableSteps: {
            type: Boolean,
            default: true,
        },
        allowFutureSteps: {
            type: Boolean,
            default: true,
        },
        showActions: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        eyebrow: {
            type: String,
            default: '',
        },
        previousLabel: {
            type: String,
            default: 'Voltar',
        },
        nextLabel: {
            type: String,
            default: 'Próximo',
        },
        finishLabel: {
            type: String,
            default: 'Finalizar',
        },
        showFinishOnlyLastStep: {
            type: Boolean,
            default: true,
        },
        previousIcon: {
            type: String,
            default: 'icon-chevron-left',
        },
        nextIcon: {
            type: String,
            default: 'icon-chevron-right',
        },
        finishIcon: {
            type: String,
            default: 'icon-check',
        },
        optionalLabel: {
            type: String,
            default: 'Opcional',
        },
        showRequiredBadge: {
            type: Boolean,
            default: false,
        },
        requiredLabel: {
            type: String,
            default: 'Obrigatorio',
        },
        completedLabel: {
            type: String,
            default: 'Concluido',
        },
        nextDisabled: {
            type: Boolean,
            default: false,
        },
        finishDisabled: {
            type: Boolean,
            default: false,
        },
        canChangeStep: {
            type: Function,
            default: null,
        },
        beforeChange: {
            type: Function,
            default: null,
        },
    },
    emits: ['update:modelValue', 'before-change', 'change', 'change-blocked', 'next', 'previous', 'finish'],
    data() {
        return {
            internalValue: this.modelValue,
            isChanging: false,
            stepPulse: false,
            stepPulseTimeout: null,
        };
    },
    computed: {
        normalizedSteps() {
            return this.steps.map((step, index) => {
                if (typeof step === 'string') {
                    return {
                        key: step,
                        label: step,
                        slot: step,
                    };
                }

                const key = step.id || step.key || index;

                return {
                    ...step,
                    key,
                    label: step.label || step.title || `Etapa ${index + 1}`,
                    slot: step.slot || key,
                };
            });
        },
        activeIndex() {
            const indexByKey = this.normalizedSteps.findIndex((step) => step.key === this.internalValue);

            if (indexByKey >= 0) return indexByKey;
            if (typeof this.internalValue === 'number') return Math.min(Math.max(this.internalValue, 0), this.normalizedSteps.length - 1);

            return 0;
        },
        activeStep() {
            return this.normalizedSteps[this.activeIndex] || {};
        },
        isFirstStep() {
            return this.activeIndex === 0;
        },
        isLastStep() {
            return this.activeIndex === this.normalizedSteps.length - 1;
        },
        showFinishButton() {
            return this.isLastStep || !this.showFinishOnlyLastStep;
        },
        stepPositionLabel() {
            return `${this.activeIndex + 1} de ${this.normalizedSteps.length}`;
        },
        stepProgressPercent() {
            if (this.normalizedSteps.length <= 1) return '100%';

            return `${(this.activeIndex / (this.normalizedSteps.length - 1)) * 100}%`;
        },
        wizardClasses() {
            return {
                'wizard-component--left': this.navigationPosition === 'left',
                'wizard-component--top': this.navigationPosition === 'top',
                'wizard-component--step-pulse': this.stepPulse,
            };
        },
    },
    watch: {
        modelValue(value) {
            this.internalValue = value;
        },
    },
    beforeUnmount() {
        if (this.stepPulseTimeout) clearTimeout(this.stepPulseTimeout);
    },
    methods: {
        getStepClasses(step, index) {
            return {
                'wizard-component__step--active': index === this.activeIndex,
                'wizard-component__step--done': index < this.activeIndex || step.completed,
                'wizard-component__step--error': step.error,
                'wizard-component__step--optional': step.optional,
                'wizard-component__step--disabled': !this.canClickStep(step, index),
            };
        },
        canClickStep(step, index) {
            if (!this.clickableSteps || step.disabled) return false;
            if (!this.allowFutureSteps && index > this.activeIndex) return false;

            return true;
        },
        buildStepChangeContext(nextIndex, trigger) {
            const currentIndex = this.activeIndex;
            const nextStep = this.normalizedSteps[nextIndex];
            const currentStep = this.activeStep;
            const direction = nextIndex > currentIndex ? 'forward' : 'backward';

            return {
                nextStep,
                currentStep,
                nextIndex,
                currentIndex,
                direction,
                trigger,
                isForward: direction === 'forward',
                isBackward: direction === 'backward',
            };
        },
        async canProceedWithStepChange(context) {
            this.$emit('before-change', context);

            if (!this.beforeChange && !this.canChangeStep) return true;

            this.isChanging = true;

            try {
                if (this.beforeChange) {
                    return await this.beforeChange(context) !== false;
                }

                return await this.canChangeStep(
                    context.nextStep,
                    context.currentStep,
                    context.nextIndex,
                    context.currentIndex,
                    context,
                ) !== false;
            } finally {
                this.isChanging = false;
            }
        },
        async changeStep(nextIndex, trigger = 'step') {
            if (nextIndex === this.activeIndex || nextIndex < 0 || nextIndex >= this.normalizedSteps.length) return;

            const context = this.buildStepChangeContext(nextIndex, trigger);
            const canChange = await this.canProceedWithStepChange(context);

            if (!canChange) {
                this.$emit('change-blocked', context);
                return;
            }

            const value = context.nextStep.key;
            this.internalValue = value;
            this.playStepPulse();
            this.$emit('update:modelValue', value);
            this.$emit('change', context.nextStep, context.currentStep, context.nextIndex, context.currentIndex, context);
        },
        playStepPulse() {
            if (this.stepPulseTimeout) clearTimeout(this.stepPulseTimeout);

            this.stepPulse = false;
            this.$nextTick(() => {
                this.stepPulse = true;
                this.stepPulseTimeout = setTimeout(() => {
                    this.stepPulse = false;
                    this.stepPulseTimeout = null;
                }, 520);
            });
        },
        goToStep(index) {
            if (!this.canClickStep(this.normalizedSteps[index], index)) return;

            this.changeStep(index, 'step');
        },
        nextStep() {
            const currentStep = this.activeStep;
            this.$emit('next', currentStep, this.activeIndex);
            this.changeStep(this.activeIndex + 1, 'next');
        },
        previousStep() {
            const currentStep = this.activeStep;
            this.$emit('previous', currentStep, this.activeIndex);
            this.changeStep(this.activeIndex - 1, 'previous');
        },
        reset(stepKeyOrIndex = 0) {
            const nextIndex = typeof stepKeyOrIndex === 'number'
                ? stepKeyOrIndex
                : this.normalizedSteps.findIndex((step) => step.key === stepKeyOrIndex);

            const safeIndex = nextIndex >= 0 && nextIndex < this.normalizedSteps.length ? nextIndex : 0;
            const nextStep = this.normalizedSteps[safeIndex];

            if (!nextStep) return;

            const currentStep = this.activeStep;
            const currentIndex = this.activeIndex;

            this.internalValue = nextStep.key;
            this.playStepPulse();
            this.$emit('update:modelValue', nextStep.key);
            this.$emit('change', nextStep, currentStep, safeIndex, currentIndex, {
                nextStep,
                currentStep,
                nextIndex: safeIndex,
                currentIndex,
                direction: 'reset',
                trigger: 'reset',
                isForward: false,
                isBackward: false,
            });
        },
    },
};
</script>

<style scoped>
.wizard-component {
    overflow: hidden;
}

.wizard-component__header {
    align-items: center;
    border-bottom: 1px solid #e6eaee;
    display: flex;
    justify-content: space-between;
    padding: 1.15rem 1.25rem .95rem;
}

.wizard-component__eyebrow {
    color: #0e5e4e;
    display: block;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .08rem;
    margin-bottom: .35rem;
    text-transform: uppercase;
}

.wizard-component__title {
    color: #27343f;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
}

.wizard-component__subtitle {
    color: #777b82;
    font-size: .84rem;
    line-height: 1.45;
    margin: .35rem 0 0;
}

.wizard-component__body {
    display: flex;
    flex-direction: column;
}

.wizard-component__mobile-step-header {
    display: none;
}

.wizard-component__nav {
    display: grid;
    gap: .65rem;
    padding: 1rem 1.25rem .25rem;
}

.wizard-component--top .wizard-component__nav {
    border-bottom: 1px solid #d7dbe2;
    gap: 1.15rem;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    margin: 1rem 1.25rem .25rem;
    overflow: visible;
    padding: 0 0 1rem;
}

.wizard-component--top .wizard-component__step {
    align-items: center;
    background: #f6f8fa;
    border-color: #e1e6eb;
    border-radius: 12px;
    min-height: 68px;
    padding: .65rem .72rem;
    position: relative;
}

.wizard-component--top .wizard-component__step:before {
    border-radius: 15px;
    content: "";
    inset: -4px;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    transition: opacity .2s ease;
}

.wizard-component--top .wizard-component__step:hover:not(:disabled) {
    background: #f9fbfc;
    border-color: #e7edf0;
    box-shadow: 0 6px 16px rgba(39, 52, 63, .05);
    transform: none;
}

.wizard-component--top .wizard-component__step:not(:last-child):after {
    background: #d2dbe2;
    border-radius: 999px;
    content: "";
    height: 4px;
    left: calc(100% + .18rem);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: .8rem;
    z-index: 0;
}

.wizard-component--top .wizard-component__step--done:not(:last-child):after {
    background: linear-gradient(90deg, #0e5e4e 0%, #2fb7a3 42%, #6fb7d8 72%, #6278c8 100%);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, .6), 0 0 10px rgba(111, 183, 216, .2), 0 0 8px rgba(47, 183, 163, .16);
}

.wizard-component--top .wizard-component__step-marker {
    background: #f1f5f3;
    border: 1px solid #d9e2df;
    border-radius: 999px;
    box-shadow: 0 0 0 5px #f6f8fa, 0 4px 12px rgba(39, 52, 63, .08);
    color: #6f7f7a;
    flex: 0 0 36px;
    height: 36px;
    margin-bottom: 0;
    margin-right: .72rem;
    position: relative;
    z-index: 1;
}

.wizard-component--top .wizard-component__step-content {
    min-width: 0;
    position: relative;
    z-index: 2;
}

.wizard-component--top .wizard-component__step--active {
    background: linear-gradient(135deg, #f8fbfa 0%, #f1f5f4 100%);
    border-color: #9dbdb4;
    box-shadow: 0 10px 22px rgba(39, 52, 63, .08), inset 0 1px 0 rgba(255, 255, 255, .72);
}

.wizard-component--top .wizard-component__step--active:before {
    background: linear-gradient(135deg, rgba(14, 94, 78, .16), rgba(66, 199, 255, .12), rgba(109, 93, 252, .1));
    opacity: 1;
}

.wizard-component--step-pulse.wizard-component--top .wizard-component__step--active:before {
    animation: wizard-step-ring-in .48s cubic-bezier(.22, 1, .36, 1);
}

.wizard-component--top .wizard-component__step--active .wizard-component__step-marker {
    background: #edf4f1;
    border-color: #148f82;
    box-shadow: 0 0 0 5px #f6f8fa, 0 0 0 8px rgba(20, 143, 130, .24), 0 8px 16px rgba(20, 143, 130, .14);
    color: #0e5e4e;
}

.wizard-component--top .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker {
    background: #edf4f1;
    border-color: #c7d8d2;
    box-shadow: 0 0 0 5px #f6f8fa;
    color: #5d716b;
}

.wizard-component--top .wizard-component__step--optional:not(.wizard-component__step--active):not(.wizard-component__step--done) .wizard-component__step-marker {
    background: #f5faf8;
    border-color: #bfd7cf;
    border-style: dashed;
    color: #6f837d;
}

.wizard-component--left .wizard-component__body {
    align-items: stretch;
    flex-direction: row;
}

.wizard-component--left .wizard-component__nav {
    border-right: 1px solid #e6eaee;
    flex: 0 0 275px;
    grid-template-columns: 1fr;
    padding: 1rem;
}

.wizard-component__step {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e1e6eb;
    border-radius: 12px;
    color: #383737;
    cursor: pointer;
    display: flex;
    min-height: 68px;
    padding: .75rem;
    text-align: left;
    transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.wizard-component__step:hover:not(:disabled) {
    border-color: rgba(14, 94, 78, .35);
    box-shadow: 0 10px 20px rgba(14, 94, 78, .08);
    transform: translateY(-1px);
}

.wizard-component__step:disabled {
    cursor: not-allowed;
}

.wizard-component__step-marker {
    align-items: center;
    background: #eef3f5;
    border-radius: 10px;
    color: #0e5e4e;
    display: inline-flex;
    flex: 0 0 38px;
    font-size: .95rem;
    font-weight: 700;
    height: 38px;
    justify-content: center;
    margin-right: .75rem;
}

.wizard-component__step-marker i {
    font-size: 1rem;
}

.wizard-component__step-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.wizard-component__step-label {
    font-size: .86rem;
    font-weight: 700;
    line-height: 1.25;
}

.wizard-component__step-label-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: .4rem;
}

.wizard-component__step-optional {
    background: #eef8ff;
    border: 1px solid #c7e6f7;
    border-radius: 999px;
    color: #2f80a7;
    font-size: .64rem;
    font-weight: 700;
    letter-spacing: .02rem;
    line-height: 1;
    padding: .22rem .42rem;
    text-transform: uppercase;
}

.wizard-component__step-completed-label {
    background: #edf8f4;
    border: 1px solid #c4e4d7;
    border-radius: 999px;
    color: #0e5e4e;
    font-size: .64rem;
    font-weight: 700;
    letter-spacing: .02rem;
    line-height: 1;
    padding: .22rem .42rem;
    text-transform: uppercase;
}

.wizard-component__step-required-label {
    background: #fff1f1;
    border: 1px solid #f3b8b8;
    border-radius: 999px;
    color: #b42318;
    font-size: .64rem;
    font-weight: 700;
    letter-spacing: .02rem;
    line-height: 1;
    padding: .22rem .42rem;
    text-transform: uppercase;
}

.wizard-component__step-required-asterisk {
    color: #d64c36;
    display: none;
    font-size: .9rem;
    font-weight: 800;
    line-height: 1;
}

.wizard-component__step-description {
    color: #777b82;
    font-size: .74rem;
    line-height: 1.35;
    margin-top: .2rem;
}

.wizard-component__step--active {
    background: #f8fbfa;
    border-color: #0e5e4e;
    box-shadow: 0 10px 25px rgba(14, 94, 78, .12);
}

.wizard-component__step--active .wizard-component__step-marker,
.wizard-component__step--done .wizard-component__step-marker {
    background: #0e5e4e;
    color: #ffffff;
}

.wizard-component__step--done:not(.wizard-component__step--active) {
    border-color: rgba(14, 94, 78, .22);
}

.wizard-component__step--error {
    border-color: #d64c36;
}

.wizard-component__step--error .wizard-component__step-marker {
    background: #d64c36;
    color: #ffffff;
}

.wizard-component__step--disabled {
    opacity: .58;
}

.wizard-component__content {
    flex: 1;
    min-width: 0;
    padding: 1.25rem;
}

.wizard-component__footer {
    align-items: center;
    border-top: 1px solid #e6eaee;
    display: flex;
    justify-content: space-between;
    padding: 1rem 1.25rem;
}

.wizard-component__footer-actions {
    align-items: center;
    display: flex;
    gap: .5rem;
}

.wizard-component__action {
    align-items: center;
    border-radius: 11px;
    display: inline-flex;
    font-weight: 700;
    min-height: 38px;
    padding: .55rem 1rem;
    transition: background .18s ease, border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.wizard-component__action:not(:disabled):hover {
    transform: translateY(-1px);
}

.wizard-component__action--previous {
    background: #f6f8fa;
    border: 1px solid #dce3e8;
    color: #45525d;
    box-shadow: 0 6px 14px rgba(39, 52, 63, .06), inset 0 1px 0 rgba(255, 255, 255, .75);
}

.wizard-component__action--previous:not(:disabled):hover {
    background: #ffffff;
    border-color: #cfd9df;
    color: #27343f;
    box-shadow: 0 10px 20px rgba(39, 52, 63, .08), inset 0 1px 0 rgba(255, 255, 255, .85);
}

.wizard-component__action--finish {
    background: linear-gradient(135deg, #f4fbf8 0%, #e8f5f0 100%);
    border-color: rgba(14, 94, 78, .34);
    box-shadow: 0 9px 20px rgba(14, 94, 78, .1), inset 0 1px 0 rgba(255, 255, 255, .8);
    color: #0e5e4e;
}

.wizard-component__action--finish:not(:disabled):hover {
    background: linear-gradient(135deg, #eaf7f2 0%, #d9eee7 100%);
    border-color: rgba(14, 94, 78, .48);
    color: #0b4f42;
    box-shadow: 0 12px 24px rgba(14, 94, 78, .14), inset 0 1px 0 rgba(255, 255, 255, .85);
}

.wizard-component__action--finish:focus,
.wizard-component__action--finish.focus {
    box-shadow: 0 0 0 .2rem rgba(24, 165, 143, .22), 0 10px 22px rgba(14, 94, 78, .18);
}

.wizard-component__footer .btn i:first-child {
    margin-right: .35rem;
}

.wizard-component__footer .btn i:last-child {
    margin-left: .35rem;
}

.wizard-component--left {
    background: transparent;
    border: 0;
    box-shadow: none;
    overflow: visible;
}

.wizard-component--left .wizard-component__header {
    background: #f6f8fa;
    border: 0;
    border-bottom: 1px solid #d7dbe2;
    border-radius: 14px;
    box-shadow: 0 8px 24px rgba(39, 52, 63, .08);
    margin-bottom: .65rem;
}

.wizard-component--left .wizard-component__body {
    align-items: flex-start;
    gap: .65rem;
}

.wizard-component--left .wizard-component__nav {
    background: linear-gradient(180deg, #fbfcfd 0%, #f6f8fa 100%);
    border: 0;
    border-bottom: 1px solid #d7dbe2;
    border-radius: 18px;
    box-shadow: 0 12px 32px rgba(39, 52, 63, .08);
    flex: 0 0 306px;
    gap: .25rem;
    padding: .85rem .75rem;
    position: sticky;
    top: 4.4rem;
}

.wizard-component--left .wizard-component__step {
    background: transparent;
    border: 0;
    border-radius: 0;
    min-height: 68px;
    padding: .35rem 0 .35rem .35rem;
    position: relative;
    z-index: 1;
}

.wizard-component--left .wizard-component__step:before,
.wizard-component--left .wizard-component__step:after {
    background: #d2dbe2;
    border-radius: 999px;
    content: "";
    left: calc(.35rem + 16px);
    position: absolute;
    width: 4px;
    z-index: 0;
}

.wizard-component--left .wizard-component__step:before {
    bottom: calc(50% + 18px);
    top: -.25rem;
}

.wizard-component--left .wizard-component__step:after {
    bottom: -.25rem;
    top: calc(50% + 18px);
}

.wizard-component--left .wizard-component__step:first-child:before,
.wizard-component--left .wizard-component__step:last-child:after {
    display: none;
}

.wizard-component--left .wizard-component__step:hover:not(:disabled) {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    transform: none;
}

.wizard-component--left .wizard-component__step-marker {
    background: #f1f5f3;
    border: 1px solid #d9e2df;
    border-radius: 999px;
    box-shadow: 0 0 0 5px #f6f8fa, 0 4px 12px rgba(39, 52, 63, .08);
    color: #6f7f7a;
    flex: 0 0 36px;
    font-size: .8rem;
    height: 36px;
    margin-left: 0;
    margin-right: .75rem;
    position: relative;
    z-index: 1;
}

.wizard-component--left .wizard-component__step--optional:not(.wizard-component__step--active):not(.wizard-component__step--done) .wizard-component__step-marker {
    background: #f5faf8;
    border-color: #bfd7cf;
    border-style: dashed;
    color: #6f837d;
}

.wizard-component--left .wizard-component__step--optional:not(.wizard-component__step--active) .wizard-component__step-content {
    border-color: rgba(47, 128, 167, .12);
}

.wizard-component--left .wizard-component__step-marker i {
    font-size: .9rem;
}

.wizard-component--left .wizard-component__step-content {
    border: 1px solid rgba(47, 128, 167, .12);
    border-radius: 13px;
    flex: 1;
    padding: .72rem .85rem;
    position: relative;
    transition: background .2s ease, border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}

.wizard-component--left .wizard-component__step-content:before {
    border-radius: 15px;
    content: "";
    inset: -4px;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    transition: opacity .2s ease;
}

.wizard-component--left .wizard-component__step-label {
    color: #27343f;
    font-size: .86rem;
    font-weight: 700;
}

.wizard-component--left .wizard-component__step-description {
    color: #8a949d;
    font-size: .73rem;
}

.wizard-component--left .wizard-component__step--active {
    background: transparent;
    box-shadow: none;
}

.wizard-component--left .wizard-component__step:hover:not(:disabled) .wizard-component__step-content {
    background: #f9fbfc;
    border-color: #e7edf0;
    box-shadow: 0 6px 16px rgba(39, 52, 63, .05);
}

.wizard-component--left .wizard-component__step--active .wizard-component__step-content {
    background: linear-gradient(135deg, #f8fbfa 0%, #f1f5f4 100%);
    border-color: #9dbdb4;
    box-shadow: 0 10px 22px rgba(39, 52, 63, .08), inset 0 1px 0 rgba(255, 255, 255, .72);
    transform: translateX(2px);
}

.wizard-component--left .wizard-component__step--active .wizard-component__step-content:before {
    background: linear-gradient(135deg, rgba(14, 94, 78, .16), rgba(66, 199, 255, .12), rgba(109, 93, 252, .1));
    opacity: 1;
}

.wizard-component--step-pulse.wizard-component--left .wizard-component__step--active .wizard-component__step-content:before {
    animation: wizard-step-ring-in .48s cubic-bezier(.22, 1, .36, 1);
}

.wizard-component--step-pulse .wizard-component__step--active .wizard-component__step-marker {
    animation: wizard-step-marker-pop .42s cubic-bezier(.22, 1, .36, 1);
}

@keyframes wizard-step-ring-in {
    0% {
        opacity: 0;
        transform: scale(1.1);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes wizard-step-marker-pop {
    0% {
        transform: scale(.92);
    }

    65% {
        transform: scale(1.05);
    }

    100% {
        transform: scale(1);
    }
}

.wizard-component--left .wizard-component__step--active .wizard-component__step-marker {
    background: #edf4f1;
    border-color: #148f82;
    box-shadow: 0 0 0 5px #f6f8fa, 0 0 0 8px rgba(20, 143, 130, .24), 0 8px 16px rgba(20, 143, 130, .14);
    color: #0e5e4e;
}

.wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) {
    border-color: transparent;
}

.wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker {
    background: #edf4f1;
    border-color: #c7d8d2;
    box-shadow: 0 0 0 5px #f6f8fa;
    color: #5d716b;
}

.wizard-component--left .wizard-component__step--done:after,
.wizard-component--left .wizard-component__step--done + .wizard-component__step:before {
    background: linear-gradient(180deg, #0e5e4e 0%, #2fb7a3 42%, #6fb7d8 72%, #6278c8 100%);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, .6), 0 0 10px rgba(111, 183, 216, .2), 0 0 8px rgba(47, 183, 163, .16);
}

.wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker i,
.wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker span {
    color: #5d716b;
}

.wizard-component--left .wizard-component__step--error:before {
    background: #d64c36;
}

.wizard-component--left .wizard-component__step--error .wizard-component__step-marker {
    background: #fff4f2;
    border-color: rgba(214, 76, 54, .22);
    color: #d64c36;
}

.wizard-component--left .wizard-component__content {
    background: #f6f8fa;
    border-bottom: 1px solid #d7dbe2;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(39, 52, 63, .08);
    min-height: 360px;
    padding: 1.35rem;
}

.wizard-component--left .wizard-component__footer {
    background: #f6f8fa;
    border: 0;
    border-bottom: 1px solid #d7dbe2;
    border-radius: 14px;
    box-shadow: 0 8px 24px rgba(39, 52, 63, .08);
    margin-left: 316px;
    margin-top: .65rem;
}

:global(html.dark-theme .wizard-component ){
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    box-shadow: var(--dark-shadow) !important;
    color: var(--dark-text-soft) !important;
}

:global(html.dark-theme .wizard-component__header),
:global(html.dark-theme .wizard-component__footer ){
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component__content ){
    background: var(--dark-surface) !important;
    color: var(--dark-text-soft) !important;
}

:global(html.dark-theme .wizard-component__footer ){
    border-top-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__nav ){
    background: var(--dark-surface) !important;
}

:global(html.dark-theme .wizard-component__eyebrow),
:global(html.dark-theme .wizard-component__step-marker ){
    color: var(--dark-primary) !important;
}

:global(html.dark-theme .wizard-component__title),
:global(html.dark-theme .wizard-component__step-label ){
    color: var(--dark-text) !important;
}

:global(html.dark-theme .wizard-component__subtitle),
:global(html.dark-theme .wizard-component__step-description ){
    color: var(--dark-text-muted) !important;
}

:global(html.dark-theme .wizard-component__step-optional ){
    background: rgba(56, 189, 248, .1) !important;
    border-color: rgba(56, 189, 248, .26) !important;
    color: #9bdcf8 !important;
}

:global(html.dark-theme .wizard-component__action--previous ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
    box-shadow: 0 10px 24px rgba(2, 6, 23, .22), inset 0 1px 0 rgba(255, 255, 255, .04) !important;
}

:global(html.dark-theme .wizard-component__action--next ){
    background: var(--dark-primary) !important;
    border-color: var(--dark-primary) !important;
    color: #ffffff !important;
    box-shadow: 0 12px 26px rgba(20, 184, 166, .16), inset 0 1px 0 rgba(255, 255, 255, .08) !important;
}

:global(html.dark-theme .wizard-component__action--next:not(:disabled):hover ){
    background: var(--dark-primary-hover) !important;
    border-color: var(--dark-primary-hover) !important;
    color: #ffffff !important;
}

:global(html.dark-theme .wizard-component__action--previous:not(:disabled):hover ){
    background: var(--dark-surface-3) !important;
    border-color: var(--dark-border-strong) !important;
    color: var(--dark-text) !important;
}

:global(html.dark-theme .wizard-component__action--finish ){
    background: rgba(20, 184, 166, .12) !important;
    border-color: rgba(20, 184, 166, .36) !important;
    color: #5eead4 !important;
    box-shadow: 0 12px 26px rgba(20, 184, 166, .12), inset 0 1px 0 rgba(255, 255, 255, .04) !important;
}

:global(html.dark-theme .wizard-component__action--finish:not(:disabled):hover ){
    background: rgba(20, 184, 166, .18) !important;
    border-color: rgba(20, 184, 166, .52) !important;
    color: #99f6e4 !important;
}

:global(html.dark-theme .wizard-component__step-completed-label ){
    background: rgba(20, 184, 166, .12) !important;
    border-color: rgba(20, 184, 166, .28) !important;
    color: #5eead4 !important;
}

:global(html.dark-theme .wizard-component__step-required-label ){
    background: rgba(251, 113, 133, .12) !important;
    border-color: rgba(251, 113, 133, .32) !important;
    color: #fda4af !important;
}

:global(html.dark-theme .wizard-component__step ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

:global(html.dark-theme .wizard-component__step:hover:not(:disabled)),
:global(html.dark-theme .wizard-component__step--active ){
    border-color: rgba(20, 184, 166, .45) !important;
    box-shadow: 0 14px 32px rgba(20, 184, 166, .12) !important;
}

:global(html.dark-theme .wizard-component__step--active ){
    background: rgba(20, 184, 166, .08) !important;
}

:global(html.dark-theme .wizard-component__step-marker ){
    background: var(--dark-surface-3) !important;
}

:global(html.dark-theme .wizard-component__step--active .wizard-component__step-marker),
:global(html.dark-theme .wizard-component__step--done .wizard-component__step-marker ){
    background: var(--dark-primary) !important;
    color: #ffffff !important;
}

:global(html.dark-theme .wizard-component__step--done:not(.wizard-component__step--active) ){
    border-color: rgba(20, 184, 166, .3) !important;
}

:global(html.dark-theme .wizard-component__step--error ){
    border-color: var(--dark-danger) !important;
}

:global(html.dark-theme .wizard-component__step--error .wizard-component__step-marker ){
    background: var(--dark-danger) !important;
    color: #ffffff !important;
}

:global(html.dark-theme .wizard-component--left ){
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__header),
:global(html.dark-theme .wizard-component--left .wizard-component__nav),
:global(html.dark-theme .wizard-component--left .wizard-component__content),
:global(html.dark-theme .wizard-component--left .wizard-component__footer ){
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    box-shadow: var(--dark-shadow) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step ){
    background: transparent !important;
    border-color: transparent !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step:hover:not(:disabled)),
:global(html.dark-theme .wizard-component--left .wizard-component__step--active ){
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step-marker ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border-strong) !important;
    box-shadow: 0 0 0 5px var(--dark-surface), 0 4px 12px rgba(2, 6, 23, .22) !important;
    color: var(--dark-text-muted) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--optional:not(.wizard-component__step--active):not(.wizard-component__step--done) .wizard-component__step-marker ){
    background: rgba(136, 169, 161, .08) !important;
    border-color: rgba(136, 169, 161, .32) !important;
    border-style: dashed !important;
    color: var(--dark-text-muted) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--optional:not(.wizard-component__step--active) .wizard-component__step-content ){
    border-color: rgba(56, 189, 248, .16) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step:before),
:global(html.dark-theme .wizard-component--left .wizard-component__step:after ){
    background: var(--dark-border-strong) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step:hover:not(:disabled) .wizard-component__step-content ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    box-shadow: 0 10px 24px rgba(2, 6, 23, .2) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step-content ){
    border-color: rgba(56, 189, 248, .16) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--active .wizard-component__step-content ){
    background: linear-gradient(135deg, var(--dark-surface-2) 0%, var(--dark-surface-3) 100%) !important;
    border-color: rgba(136, 169, 161, .26) !important;
    box-shadow: 0 12px 26px rgba(2, 6, 23, .24), inset 0 1px 0 rgba(255, 255, 255, .04) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--active .wizard-component__step-content:before ){
    background: linear-gradient(135deg, rgba(20, 184, 166, .14), rgba(66, 199, 255, .1), rgba(109, 93, 252, .1)) !important;
    opacity: 1 !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--active .wizard-component__step-marker),
:global(html.dark-theme .wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker ){
    background: rgba(136, 169, 161, .14) !important;
    border-color: rgba(136, 169, 161, .3) !important;
    color: #a8bbb5 !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--done:after),
:global(html.dark-theme .wizard-component--left .wizard-component__step--done + .wizard-component__step:before ){
    background: linear-gradient(180deg, var(--dark-primary) 0%, #2fb7a3 42%, #6fb7d8 72%, #6278c8 100%) !important;
    box-shadow: 0 0 10px rgba(111, 183, 216, .22), 0 0 8px rgba(47, 183, 163, .18) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker ){
    background: rgba(136, 169, 161, .14) !important;
    border-color: rgba(136, 169, 161, .3) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker i),
:global(html.dark-theme .wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker span ){
    color: #a8bbb5 !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--done:not(.wizard-component__step--active) ){
    border-color: transparent !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--error:before ){
    background: var(--dark-danger) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--error .wizard-component__step-marker ){
    background: rgba(251, 113, 133, .12) !important;
    border-color: rgba(251, 113, 133, .28) !important;
    color: var(--dark-danger) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__nav ){
    border-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step ){
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step:hover:not(:disabled) ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    box-shadow: 0 10px 24px rgba(2, 6, 23, .2) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step:not(:last-child):after ){
    background: var(--dark-border-strong) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step--done:not(:last-child):after ){
    background: linear-gradient(90deg, var(--dark-primary) 0%, #2fb7a3 42%, #6fb7d8 72%, #6278c8 100%) !important;
    box-shadow: 0 0 10px rgba(111, 183, 216, .22), 0 0 8px rgba(47, 183, 163, .18) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step-marker ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border-strong) !important;
    box-shadow: 0 0 0 5px var(--dark-surface), 0 4px 12px rgba(2, 6, 23, .22) !important;
    color: var(--dark-text-muted) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step--active ){
    background: linear-gradient(135deg, var(--dark-surface-2) 0%, var(--dark-surface-3) 100%) !important;
    border-color: rgba(136, 169, 161, .26) !important;
    box-shadow: 0 12px 26px rgba(2, 6, 23, .24), inset 0 1px 0 rgba(255, 255, 255, .04) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step--active:before ){
    background: linear-gradient(135deg, rgba(20, 184, 166, .14), rgba(66, 199, 255, .1), rgba(109, 93, 252, .1)) !important;
    opacity: 1 !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step--active .wizard-component__step-marker),
:global(html.dark-theme .wizard-component--top .wizard-component__step--done:not(.wizard-component__step--active) .wizard-component__step-marker ){
    background: rgba(136, 169, 161, .14) !important;
    border-color: rgba(136, 169, 161, .3) !important;
    color: #a8bbb5 !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step--optional:not(.wizard-component__step--active):not(.wizard-component__step--done) .wizard-component__step-marker ){
    background: rgba(136, 169, 161, .08) !important;
    border-color: rgba(136, 169, 161, .32) !important;
    border-style: dashed !important;
    color: var(--dark-text-muted) !important;
}

@media (max-width: 991.98px) {
    .wizard-component--left .wizard-component__body {
        flex-direction: column;
        gap: .65rem;
    }

    .wizard-component--left .wizard-component__nav {
        border-right: 0;
        border-bottom: 1px solid #d7dbe2;
        flex-basis: auto;
        grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
        padding: 1rem 1.25rem .25rem;
        position: static;
    }

    .wizard-component--left .wizard-component__footer {
        margin-left: 0;
    }
}

@media (max-width: 575.98px) {
    .wizard-component__header,
    .wizard-component__footer {
        align-items: stretch;
        flex-direction: column;
        gap: .75rem;
    }

    .wizard-component__footer {
        background: #ffffff;
        bottom: 0;
        box-shadow: 0 -10px 24px rgba(39, 52, 63, .08);
        padding: .85rem;
        position: sticky;
        z-index: 5;
    }

    .wizard-component__mobile-step-header {
        align-items: center;
        background: linear-gradient(180deg, #fbfcfd 0%, #f5f8f7 100%);
        border-bottom: 1px solid #dde6e2;
        display: grid;
        gap: .5rem;
        grid-template-columns: 34px minmax(0, 1fr) 34px;
        padding: .8rem .85rem .72rem;
        position: relative;
    }

    .wizard-component__mobile-step-card {
        background: transparent;
        border: 0;
        border-radius: 0;
        box-shadow: none;
        min-width: 0;
        padding: 0 .1rem;
    }

    .wizard-component__mobile-step-topline {
        align-items: center;
        display: flex;
        gap: .45rem;
        justify-content: flex-start;
        margin-bottom: .45rem;
    }

    .wizard-component__mobile-step-counter {
        color: #728079;
        font-size: .72rem;
        font-weight: 800;
        letter-spacing: .05rem;
        text-transform: uppercase;
    }

    .wizard-component__mobile-step-main {
        align-items: flex-start;
        display: flex;
        min-width: 0;
    }

    .wizard-component__mobile-step-marker {
        align-items: center;
        background: #eaf4f0;
        border: 1px solid #c5ddd5;
        border-radius: 999px;
        box-shadow: 0 0 0 4px rgba(234, 244, 240, .55);
        color: #0e5e4e;
        display: inline-flex;
        flex: 0 0 36px;
        font-size: .84rem;
        font-weight: 800;
        height: 36px;
        justify-content: center;
        margin-right: .68rem;
        margin-top: .05rem;
    }

    .wizard-component__mobile-step-text {
        min-width: 0;
    }

    .wizard-component__mobile-step-text h6 {
        color: #27343f;
        font-size: 1.02rem;
        font-weight: 800;
        line-height: 1.2;
        margin: 0;
    }

    .wizard-component__mobile-step-text p {
        color: #7a858b;
        font-size: .78rem;
        line-height: 1.32;
        margin: .24rem 0 0;
    }

    .wizard-component__mobile-step-progress {
        background: #dfe9e5;
        border-radius: 999px;
        bottom: 0;
        height: 3px;
        left: 0;
        margin-top: 0;
        overflow: hidden;
        position: absolute;
        right: 0;
    }

    .wizard-component__mobile-step-progress span {
        background: linear-gradient(90deg, #0e5e4e 0%, #2fb7a3 56%, #6fb7d8 100%);
        border-radius: inherit;
        display: block;
        height: 100%;
        min-width: 16%;
        transition: width .24s ease;
    }

    .wizard-component__mobile-step-arrow {
        align-items: center;
        align-self: center;
        background: rgba(255, 255, 255, .72);
        border: 1px solid rgba(206, 219, 214, .85);
        border-radius: 999px;
        box-shadow: 0 5px 12px rgba(39, 52, 63, .06);
        color: #52615b;
        display: inline-flex;
        height: 34px;
        justify-content: center;
        padding: 0;
        transition: background .18s ease, border-color .18s ease, box-shadow .18s ease, transform .18s ease;
        width: 34px;
    }

    .wizard-component__mobile-step-arrow:not(:disabled):active {
        transform: translateY(1px);
    }

    .wizard-component__mobile-step-arrow:disabled {
        opacity: .38;
    }

    .wizard-component__nav,
    .wizard-component--left .wizard-component__nav,
    .wizard-component--top .wizard-component__nav {
        display: none;
        gap: .55rem;
        grid-template-columns: none;
        overflow-x: auto;
        padding: .85rem .85rem .75rem;
        scroll-snap-type: x mandatory;
    }

    .wizard-component--top .wizard-component__nav {
        margin: .85rem .85rem 0;
    }

    .wizard-component--top .wizard-component__step:not(:last-child):after,
    .wizard-component--left .wizard-component__step:before,
    .wizard-component--left .wizard-component__step:after {
        display: none;
    }

    .wizard-component__step,
    .wizard-component--left .wizard-component__step,
    .wizard-component--top .wizard-component__step {
        align-items: center;
        background: #f6f8fa;
        border: 1px solid #e1e6eb;
        border-radius: 12px;
        flex: 0 0 auto;
        min-height: 50px;
        min-width: 148px;
        padding: .48rem .82rem;
        scroll-snap-align: start;
    }

    .wizard-component--left .wizard-component__step-content,
    .wizard-component--top .wizard-component__step-content {
        background: transparent;
        border: 0;
        box-shadow: none;
        padding: 0;
        transform: none;
    }

    .wizard-component--left .wizard-component__step-content:before,
    .wizard-component--top .wizard-component__step:before,
    .wizard-component--left .wizard-component__step--active .wizard-component__step-content:before,
    .wizard-component--top .wizard-component__step--active:before {
        display: none;
    }

    .wizard-component__step-marker,
    .wizard-component--left .wizard-component__step-marker,
    .wizard-component--top .wizard-component__step-marker {
        flex-basis: 32px;
        height: 32px;
        margin-bottom: 0;
        margin-right: .65rem;
    }

    .wizard-component__step-label {
        font-size: .78rem;
        white-space: nowrap;
    }

    .wizard-component__step-description,
    .wizard-component__step-optional,
    .wizard-component__step-required-label,
    .wizard-component__step-completed-label {
        display: none;
    }

    .wizard-component__mobile-step-topline .wizard-component__step-optional,
    .wizard-component__mobile-step-topline .wizard-component__step-required-label,
    .wizard-component__mobile-step-topline .wizard-component__step-completed-label {
        display: inline-flex;
        flex: 0 0 auto;
    }

    .wizard-component__step--active,
    .wizard-component--left .wizard-component__step--active,
    .wizard-component--top .wizard-component__step--active {
        background: #f8fafb;
        border-color: #d7e3df;
        box-shadow: 0 5px 12px rgba(39, 52, 63, .06);
    }

    .wizard-component--left .wizard-component__step--active .wizard-component__step-content,
    .wizard-component--top .wizard-component__step--active .wizard-component__step-content {
        background: transparent;
        border: 0;
        box-shadow: none;
        transform: none;
    }

    .wizard-component__step-required-asterisk {
        display: inline-flex;
    }

    :global(html.dark-theme .wizard-component__step),
    :global(html.dark-theme .wizard-component--left .wizard-component__step),
    :global(html.dark-theme .wizard-component--top .wizard-component__step ){
        background: var(--dark-surface) !important;
        border-color: var(--dark-border) !important;
    }

    :global(html.dark-theme .wizard-component__step--active),
    :global(html.dark-theme .wizard-component--left .wizard-component__step--active),
    :global(html.dark-theme .wizard-component--top .wizard-component__step--active ){
        background: var(--dark-surface-2) !important;
        border-color: rgba(136, 169, 161, .24) !important;
        box-shadow: 0 8px 18px rgba(2, 6, 23, .2) !important;
    }

    :global(html.dark-theme .wizard-component--left .wizard-component__step--active .wizard-component__step-content),
    :global(html.dark-theme .wizard-component--top .wizard-component__step--active .wizard-component__step-content ){
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        transform: none !important;
    }

    :global(html.dark-theme .wizard-component__step-required-asterisk ){
        color: #fda4af !important;
    }

    .wizard-component__content {
        padding: .45rem;
    }

    .wizard-component__footer-actions {
        flex-direction: column;
        gap: .6rem;
        width: 100%;
    }

    .wizard-component__footer .btn,
    .wizard-component__footer-actions .btn {
        flex: 0 0 auto;
        justify-content: center;
        width: 100%;
    }

    :global(html.dark-theme .wizard-component__footer ){
        background: var(--dark-surface) !important;
        box-shadow: 0 -10px 24px rgba(2, 6, 23, .28) !important;
    }

    :global(html.dark-theme .wizard-component__mobile-step-header ){
        background: linear-gradient(180deg, var(--dark-surface-2) 0%, var(--dark-surface) 100%) !important;
        border-color: var(--dark-border) !important;
    }

    :global(html.dark-theme .wizard-component__mobile-step-card ){
        background: transparent !important;
        border-color: transparent !important;
        box-shadow: none !important;
    }

    :global(html.dark-theme .wizard-component__mobile-step-counter),
    :global(html.dark-theme .wizard-component__mobile-step-text p ){
        color: var(--dark-text-soft) !important;
    }

    :global(html.dark-theme .wizard-component__mobile-step-text h6 ){
        color: var(--dark-text) !important;
    }

    :global(html.dark-theme .wizard-component__mobile-step-marker),
    :global(html.dark-theme .wizard-component__mobile-step-arrow ){
        background: rgba(255, 255, 255, .04) !important;
        border-color: var(--dark-border) !important;
        color: var(--dark-text) !important;
        box-shadow: 0 5px 12px rgba(2, 6, 23, .2) !important;
    }

    :global(html.dark-theme .wizard-component__mobile-step-progress ){
        background: var(--dark-border) !important;
    }

    .wizard-component--left .wizard-component__header,
    .wizard-component--left .wizard-component__content,
    .wizard-component--left .wizard-component__footer {
        border-radius: 12px;
    }

    .wizard-component--left .wizard-component__nav {
        border-radius: 12px;
    }

    .wizard-component--left .wizard-component__step {
        min-height: 58px;
    }
}

:global(html.dark-theme .wizard-component__nav),
:global(html.dark-theme .wizard-component--top .wizard-component__nav),
:global(html.dark-theme .wizard-component--left .wizard-component__nav ){
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step:hover:not(:disabled) ){
    background: var(--dark-surface-3) !important;
    border-color: var(--dark-border-strong) !important;
}

:global(html.dark-theme .wizard-component--top .wizard-component__step--active ){
    background: linear-gradient(135deg, var(--dark-surface-2) 0%, var(--dark-surface-3) 100%) !important;
    border-color: rgba(20, 184, 166, .34) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step ){
    background: transparent !important;
    border-color: transparent !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step-content ){
    background: rgba(255, 255, 255, .02) !important;
    border-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step:hover:not(:disabled) .wizard-component__step-content ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border-strong) !important;
}

:global(html.dark-theme .wizard-component--left .wizard-component__step--active .wizard-component__step-content ){
    background: linear-gradient(135deg, var(--dark-surface-2) 0%, var(--dark-surface-3) 100%) !important;
    border-color: rgba(20, 184, 166, .34) !important;
}

:global(html.dark-theme .wizard-component),
:global(html.dark-theme .wizard-component.card ){
    background: transparent !important;
    color: var(--dark-text-soft) !important;
}

:global(html.dark-theme .wizard-component__body ){
    background: transparent !important;
}

:global(html.dark-theme .wizard-component__header),
:global(html.dark-theme .wizard-component__nav),
:global(html.dark-theme .wizard-component__content),
:global(html.dark-theme .wizard-component__footer),
:global(html.dark-theme .wizard-component__mobile-step-header ){
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

:global(html.dark-theme .wizard-component__content ){
    box-shadow: var(--dark-shadow) !important;
}

:global(html.dark-theme .wizard-component__footer ){
    box-shadow: 0 -10px 24px rgba(2, 6, 23, .28) !important;
}

:global(html.dark-theme .wizard-component__step),
:global(html.dark-theme .wizard-component--top .wizard-component__step ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
}

:global(html.dark-theme .wizard-component__mobile-step-card ){
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
}

:global(html.dark-theme .wizard-component__mobile-step-arrow),
:global(html.dark-theme .wizard-component__mobile-step-marker ){
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border-strong) !important;
    color: var(--dark-text) !important;
}

:global(html.dark-theme .wizard-component__mobile-step-progress ){
    background: var(--dark-border) !important;
}
</style>

<style>
html.dark-theme .wizard-component.card,
html.dark-theme .wizard-component {
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
    color: var(--dark-text-soft) !important;
}

html.dark-theme .wizard-component__body {
    background: transparent !important;
}

html.dark-theme .wizard-component__header,
html.dark-theme .wizard-component__nav,
html.dark-theme .wizard-component__content,
html.dark-theme .wizard-component__footer,
html.dark-theme .wizard-component__mobile-step-header {
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

html.dark-theme .wizard-component--left .wizard-component__nav,
html.dark-theme .wizard-component--left .wizard-component__content,
html.dark-theme .wizard-component--left .wizard-component__footer,
html.dark-theme .wizard-component--top .wizard-component__nav,
html.dark-theme .wizard-component--top .wizard-component__content,
html.dark-theme .wizard-component--top .wizard-component__footer {
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    box-shadow: var(--dark-shadow) !important;
}

html.dark-theme .wizard-component__step,
html.dark-theme .wizard-component--left .wizard-component__step,
html.dark-theme .wizard-component--top .wizard-component__step {
    background: transparent !important;
    border-color: transparent !important;
    color: var(--dark-text-soft) !important;
}

html.dark-theme .wizard-component__step-content,
html.dark-theme .wizard-component--left .wizard-component__step-content,
html.dark-theme .wizard-component--top .wizard-component__step-content {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

html.dark-theme .wizard-component__step--active .wizard-component__step-content,
html.dark-theme .wizard-component--left .wizard-component__step--active .wizard-component__step-content,
html.dark-theme .wizard-component--top .wizard-component__step--active .wizard-component__step-content {
    background: linear-gradient(135deg, var(--dark-surface-2) 0%, var(--dark-surface-3) 100%) !important;
    border-color: rgba(20, 184, 166, .34) !important;
    box-shadow: 0 12px 26px rgba(2, 6, 23, .24), inset 0 1px 0 rgba(255, 255, 255, .04) !important;
}

html.dark-theme .wizard-component__step-label,
html.dark-theme .wizard-component__mobile-step-text h6 {
    color: var(--dark-text) !important;
}

html.dark-theme .wizard-component__step-description,
html.dark-theme .wizard-component__mobile-step-counter,
html.dark-theme .wizard-component__mobile-step-text p {
    color: var(--dark-text-muted) !important;
}

html.dark-theme .wizard-component__step-marker,
html.dark-theme .wizard-component__mobile-step-marker,
html.dark-theme .wizard-component__mobile-step-arrow {
    background: var(--dark-surface-3) !important;
    border-color: var(--dark-border-strong) !important;
    color: var(--dark-text-soft) !important;
    box-shadow: 0 0 0 5px var(--dark-surface), 0 4px 12px rgba(2, 6, 23, .22) !important;
}

html.dark-theme .wizard-component__step--active .wizard-component__step-marker,
html.dark-theme .wizard-component__step--done .wizard-component__step-marker {
    background: var(--dark-primary) !important;
    border-color: var(--dark-primary) !important;
    color: #ffffff !important;
}

html.dark-theme .wizard-component__mobile-step-card {
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
}

html.dark-theme .wizard-component__mobile-step-progress {
    background: var(--dark-border) !important;
}

html.dark-theme .wizard-component__footer {
    box-shadow: 0 -10px 24px rgba(2, 6, 23, .28) !important;
}

html.dark-theme .wizard-component__footer .wizard-component__action:disabled,
html.dark-theme .wizard-component__footer .wizard-component__action.disabled,
html.dark-theme .wizard-component__footer .btn-primary:disabled,
html.dark-theme .wizard-component__footer .btn-primary.disabled {
    background: rgba(148, 163, 184, .1) !important;
    border-color: rgba(148, 163, 184, .18) !important;
    box-shadow: none !important;
    color: rgba(203, 213, 225, .52) !important;
    opacity: 1 !important;
}

html.dark-theme .wizard-component__footer .wizard-component__action--previous:disabled {
    background: rgba(148, 163, 184, .12) !important;
    color: rgba(203, 213, 225, .62) !important;
}

html.dark-theme .wizard-component__footer .wizard-component__action--next:not(:disabled),
html.dark-theme .wizard-component__footer .wizard-component__action--finish:not(:disabled) {
    background: var(--dark-primary) !important;
    border-color: var(--dark-primary) !important;
    color: #ffffff !important;
}

html.dark-theme .wizard-component__step-required-label,
html.dark-theme .wizard-component__step-optional,
html.dark-theme .wizard-component__step-completed-label {
    border-radius: 999px !important;
    box-shadow: none !important;
    font-size: .58rem !important;
    font-weight: 800 !important;
    letter-spacing: .035rem !important;
    line-height: 1 !important;
    padding: .22rem .42rem !important;
}

html.dark-theme .wizard-component__step-required-label {
    background: rgba(251, 113, 133, .1) !important;
    border-color: rgba(251, 113, 133, .26) !important;
    color: #fca5a5 !important;
}

html.dark-theme .wizard-component__step-optional {
    background: rgba(56, 189, 248, .1) !important;
    border-color: rgba(56, 189, 248, .24) !important;
    color: #93c5fd !important;
}

html.dark-theme .wizard-component__step-completed-label {
    background: rgba(20, 184, 166, .12) !important;
    border-color: rgba(20, 184, 166, .28) !important;
    color: #5eead4 !important;
}

html.dark-theme .wizard-component__step-label-row {
    gap: .4rem !important;
}

html.dark-theme .wizard-component--left .wizard-component__step:before,
html.dark-theme .wizard-component--left .wizard-component__step:after {
    background: rgba(148, 163, 184, .16) !important;
    box-shadow: none !important;
    width: 3px !important;
}

html.dark-theme .wizard-component--left .wizard-component__step--done:after,
html.dark-theme .wizard-component--left .wizard-component__step--done + .wizard-component__step:before {
    background: linear-gradient(180deg, rgba(20, 184, 166, .85) 0%, rgba(45, 212, 191, .48) 100%) !important;
    box-shadow: 0 0 10px rgba(20, 184, 166, .12) !important;
}

html.dark-theme .wizard-component--top .wizard-component__step:not(:last-child):after {
    background: rgba(148, 163, 184, .16) !important;
    box-shadow: none !important;
    height: 3px !important;
}

html.dark-theme .wizard-component--top .wizard-component__step--done:not(:last-child):after {
    background: linear-gradient(90deg, rgba(20, 184, 166, .85) 0%, rgba(45, 212, 191, .48) 100%) !important;
    box-shadow: 0 0 10px rgba(20, 184, 166, .12) !important;
}

html.dark-theme .wizard-component__mobile-step-progress {
    background: rgba(148, 163, 184, .16) !important;
}

html.dark-theme .wizard-component__mobile-step-progress span {
    background: linear-gradient(90deg, rgba(20, 184, 166, .9) 0%, rgba(45, 212, 191, .55) 100%) !important;
}
</style>
